import { useState, useEffect, useCallback } from 'react';
import LeadStatsStrip from '../../components/leads/LeadStatsStrip/LeadStatsStrip';
import LeadKanbanBoard from '../../components/leads/LeadKanbanBoard/LeadKanbanBoard';
import LeadDetailPanel from '../../components/leads/LeadDetailPanel/LeadDetailPanel';
import LeadFocusView from '../../components/leads/LeadFocusView/LeadFocusView';
import LeadListView from '../../components/leads/LeadListView/LeadListView';
import CreateLeadModal from '../../components/leads/CreateLeadModal/CreateLeadModal';
import LogTouchpointModal from '../../components/leads/LogTouchpointModal/LogTouchpointModal';
import { getLeads, getLeadStats, createLead, logTouchpoint, updateLeadStatus } from '../../api/leadApi';
import './LeadsPage.css';

const TERMINAL_STATUSES = ['CONVERTED', 'DROPPED'];

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('focus');
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [directLogLead, setDirectLogLead] = useState(null);
  const [toast, setToast] = useState('');
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [quickFilter, setQuickFilter] = useState(null);

  const loadLeads = useCallback(async () => {
    try {
      const data = await getLeads(undefined, true);
      setLeads(data);
      setLastRefreshed(Date.now());
    } catch (ex) {
      console.error('Failed to load leads:', ex);
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await getLeadStats();
      setStats(data);
    } catch (ex) {
      console.error('Failed to load stats:', ex);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
    loadStats();
  }, [loadLeads, loadStats]);

  // Auto-refresh every 60 seconds when on Today's Focus view
  useEffect(() => {
    if (view !== 'focus') return;
    const interval = setInterval(() => {
      loadLeads();
    }, 60000);
    return () => clearInterval(interval);
  }, [view, loadLeads]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  async function handleCreateLead(body) {
    const newLead = await createLead(body);
    setLeads(prev => [newLead, ...prev]);
    loadStats();
    showToast('Lead created!');
  }

  function handleCardClick(lead) {
    setSelectedLeadId(lead._id);
  }

  function handleLeadUpdated(updated) {
    if (updated) {
      setLeads(prev => prev.map(l => l._id === updated._id ? updated : l));
    }
    loadStats();
  }

  function handleConverted(result) {
    if (result && result.lead) {
      setLeads(prev => prev.map(l => l._id === result.lead._id ? result.lead : l));
    }
    setSelectedLeadId(null);
    loadStats();
    showToast('Lead converted to client!');
  }

  function handlePanelClose() {
    setSelectedLeadId(null);
  }

  function handleDirectLog(lead) {
    setDirectLogLead(lead);
  }

  async function handleDirectLogSaved(body) {
    const updated = await logTouchpoint(directLogLead._id, body);
    setLeads(prev => prev.map(l => l._id === updated._id ? updated : l));
    setDirectLogLead(null);
    loadStats();
    showToast('Touchpoint logged!');
  }

  async function handleKanbanStatusChange(leadId, newStatus) {
    setLeads(prev => prev.map(l => l._id === leadId ? { ...l, status: newStatus } : l));
    try {
      const updated = await updateLeadStatus(leadId, newStatus, 'current-user');
      setLeads(prev => prev.map(l => l._id === updated._id ? updated : l));
    } catch (ex) {
      loadLeads();
      showToast('Failed to update status');
    }
  }

  const activeLeads = leads.filter(l => !TERMINAL_STATUSES.includes(l.status));

  return (
    <div className="lp__page">
      <div className="lp__main">
        <LeadStatsStrip
          stats={stats}
          loading={loadingStats}
          activeFilter={quickFilter}
          onQuickFilter={setQuickFilter}
        />

        <div className="lp__toolbar">
          <div className="lp__view-tabs">
            <button
              className={`lp__view-tab ${view === 'focus' ? 'lp__view-tab--active' : ''}`}
              onClick={() => setView('focus')}
            >
              Today's Focus
            </button>
            <button
              className={`lp__view-tab ${view === 'list' ? 'lp__view-tab--active' : ''}`}
              onClick={() => setView('list')}
            >
              All Leads
            </button>
            <button
              className={`lp__view-tab ${view === 'kanban' ? 'lp__view-tab--active' : ''}`}
              onClick={() => setView('kanban')}
            >
              Pipeline
            </button>
          </div>

          <div className="lp__toolbar-right">
            {view !== 'focus' && (
              <input
                className="lp__search"
                type="text"
                placeholder="Search by name or phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            )}
            <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>+ Add Lead</button>
          </div>
        </div>

        <div className="lp__content">
          {loadingLeads ? (
            <div className="lp__loading">Loading leads...</div>
          ) : (
            <>
              {view === 'focus' && (
                <LeadFocusView
                  leads={leads}
                  onCardClick={handleCardClick}
                  onLogClick={handleDirectLog}
                  lastRefreshed={lastRefreshed}
                  filter={quickFilter}
                  onFilterChange={setQuickFilter}
                />
              )}
              {view === 'list' && (
                <LeadListView
                  leads={leads}
                  search={search}
                  onCardClick={handleCardClick}
                  quickFilter={quickFilter}
                />
              )}
              {view === 'kanban' && (
                <LeadKanbanBoard
                  leads={activeLeads}
                  search={search}
                  onCardClick={handleCardClick}
                  onStatusChange={handleKanbanStatusChange}
                  quickFilter={quickFilter}
                />
              )}
            </>
          )}
        </div>
      </div>

      {selectedLeadId && (
        <LeadDetailPanel
          leadId={selectedLeadId}
          onClose={handlePanelClose}
          onLeadUpdated={handleLeadUpdated}
          onConverted={handleConverted}
        />
      )}

      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreateLead}
        />
      )}

      {directLogLead && (
        <LogTouchpointModal
          lead={directLogLead}
          onClose={() => setDirectLogLead(null)}
          onSaved={handleDirectLogSaved}
        />
      )}

      {toast && <div className="lp__toast">{toast}</div>}
    </div>
  );
}
